import type { adminFr } from './admin.fr';

type DeepStrings<T> = { [K in keyof T]: T[K] extends string ? string : DeepStrings<T[K]> };

/** Lot L5 texts — same tree as `admin.fr.ts` (typed, and checked by `i18n.test.ts`). */
export const adminEn: DeepStrings<typeof adminFr> = {
  common: {
    identifier: 'Identifier',
    description: 'Description',
    created: 'Created',
    updated: 'Updated',
    labels: 'Labels',
    locked: 'Locked',
    yes: 'Yes',
    no: 'No',
    saved: 'Changes saved.',
    accessTitle: 'Access',
    accessEmpty: 'Nobody holds a role on this resource yet.',
    resourceNameHelp: 'Lowercase letters, digits and dashes: this name ends up in a Kubernetes name.',
    countryHelp: 'Two-letter ISO country code (FR, BE…).',
    errors: {
      resourceName: 'Lowercase letters, digits and dashes only, 63 characters at most.',
      country: 'Two uppercase letters, such as FR.',
    },
  },
  statuses: {
    running: 'Running',
    starting: 'Starting',
    pending: 'Pending',
    queued: 'Queued',
    stopped: 'Stopped',
    failed: 'Failed',
    succeeded: 'Succeeded',
    available: 'Available',
    draft: 'Draft',
    unknown: 'Unknown',
  },
  meter: {
    warning: '{{percent}} % — close to the limit',
    danger: '{{percent}} % — limit almost reached',
    valueText: '{{current}} of {{limit}} ({{percent}} %)',
  },
  notifications: {
    title: 'Activity',
    subtitle: 'What runs, workflows and agents reported to you.',
    retention: 'The server keeps the last 10, in memory.',
    searchPlaceholder: 'Search notifications…',
    unread: 'unread',
    markAllRead_one: 'Mark {{count}} as read',
    markAllRead_other: 'Mark all {{count}} as read',
    columns: { kind: 'Kind', title: 'Notification', actor: 'Agent', at: 'Received' },
    kinds: { agent: 'Agent', success: 'Success', failure: 'Failure', warning: 'Warning' },
    empty: {
      title: 'No notifications',
      description: 'Finished runs, failures and agent actions show up here.',
    },
  },
  usage: {
    resourceCost: {
      title: 'Cost over the last {{count}} days',
      empty: 'No cost measured over the last {{count}} days.',
    },
    title: 'Usage and costs',
    subtitle: 'What the tenant consumed and what it costs, per period.',
    billingRule: 'Billed per started minute, from actual start to end.',
    seePricing: 'See pricing',
    period: 'Period',
    periods: { days_one: 'Last day', days_other: 'Last {{count}} days' },
    total: 'Total for the period',
    unavailable: 'unavailable',
    byProject: 'Costs per project',
    byProjectHint:
      'Most expensive first, broken down by product type. A row opens the project’s cost details.',
    byProjectEmpty: {
      title: 'No cost per project',
      description: 'No consumption measured over this period.',
    },
    noProject: 'No project',
    unpriced: {
      count_one: '{{count}} product without a price',
      count_other: '{{count}} products without a price',
      note_one:
        '{{count}} row counts products missing from the price list as zero: the real amount is higher.',
      note_other:
        '{{count}} rows count products missing from the price list as zero: the real amount is higher.',
    },
    linesTitle: 'Consumption',
    linesSubtitle: 'Grouped by product type and reference (instance type, egress, logs).',
    projectTitle: 'Costs — {{project}}',
    projectTitle_unnamed: 'Project costs',
    projectSubtitle: 'Consumption and costs of this project.',
    projectCrumb: 'Project costs',
    backToTenant: 'See the whole tenant',
    productTypes: { INSTANCE: 'Instances', EGRESS: 'Egress', LOGS: 'Logs', SUPPORT: 'Support' },
    quantity: { minutes: '{{minutes}} min ({{hours}} h)', gigabytes: '{{value}} GB' },
    columns: {
      project: 'Project',
      cost: 'Cost',
      breakdown: 'Breakdown',
      unpriced: 'Unpriced',
      type: 'Product',
      reference: 'Reference',
      quantity: 'Quantity',
      jobs: 'Jobs',
      last: 'Last measured',
      utilization: 'Utilization',
    },
    utilization: {
      label: 'CPU utilization: {{ratio}}',
      unmeasured: 'not measured',
      unmeasuredLabel: 'CPU utilization not measured',
      cpu: 'CPU: {{used}} vCPU·h used out of {{allocated}} allocated ({{ratio}})',
      cpuUnmeasured: 'CPU: {{allocated}} vCPU·h allocated, consumption not measured',
      memory: 'Memory: {{used}} GiB·h used out of {{allocated}} allocated ({{ratio}})',
      memoryUnmeasured: 'Memory: {{allocated}} GiB·h allocated',
      coverage: 'Computed over {{coverage}} of the allocation: the workloads whose consumption was sampled.',
      low: 'Less than 30% of the reserved vCPUs are used: the reservation is oversized.',
      method:
        'Utilization = CPU consumed ÷ CPU reserved, workload by workload (runs, workspaces, endpoints).',
    },
    emptyLines: { title: 'No consumption', description: 'Nothing was measured over this period.' },
    chart: {
      perDay: 'Cost per day',
      perWeek: 'Cost per week',
      max: 'maximum {{value}}',
      week: 'week of {{date}}',
      label_one: 'Cost over {{count}} period',
      label_other: 'Cost over {{count}} periods',
      hint: 'Left and right arrows walk through the periods, Escape leaves.',
      table: 'Show the values',
      empty: 'No cost over this period.',
    },
  },
  subscription: {
    title: 'Subscription',
    subtitle:
      'The vCPUs subscribed with the publisher, the CPU quota of the run namespace and the peak actually allocated over the period.',
    vcpu: 'vCPU',
    unknown: 'Unknown',
    subscribed: {
      label: 'Subscribed vCPUs',
      unknown: 'Not set: GRAAL_BILLING_SUBSCRIPTION_VCPU (chart value billing.subscription.vcpu).',
      hint: 'The annual per-vCPU subscription, invoiced by the publisher outside the platform.',
    },
    quota: {
      label: 'Run namespace quota',
      source: 'ResourceQuota of {{namespace}}, key {{key}}.',
      overSubscribed: 'The cluster allows more vCPUs than the subscription covers.',
      forbidden:
        'graal-api may not read the ResourceQuotas of {{namespace}}: add get and list on resourcequotas to graal-api’s Role in that namespace (an infrastructure change).',
      absent: 'Namespace {{namespace}} has no ResourceQuota bounding CPU.',
      noInfrastructure: 'No Kubernetes infrastructure of the platform runs its workloads in {{namespace}}.',
      unavailable: 'Reading the quota is switched off on this installation.',
      error: 'The quota of {{namespace}} could not be read (cluster unreachable?): see graal-api’s logs.',
    },
    peak: {
      label: 'Peak allocated over the period',
      unknown:
        'The usage of the period has instance types missing from the catalogue: their size is unknown.',
      at: 'Reached on {{date}}.',
      meter: 'Peak against the subscribed vCPUs',
      inUse: 'Allocated right now: {{value}} vCPU (per the quota).',
      overSubscribed: 'The peak exceeds the subscribed vCPUs.',
    },
    unsized_one: 'Instance type missing from the catalogue, not counted in the peak: {{types}}.',
    unsized_other: 'Instance types missing from the catalogue, not counted in the peak: {{types}}.',
    method:
      'The peak adds up the vCPUs reserved by the runs, workspaces and endpoints of every tenant (instance usage) and, when the period includes today, what the quota counts as allocated now: a running workload has no usage written yet.',
  },
  capacity: {
    title: 'Tenant capacity',
    nodes: 'Nodes',
    cpus: 'vCPU',
    cpuUnit: 'vCPU',
    memory: 'Memory',
    zones: 'Zones: {{zones}} — at least {{min}} nodes.',
  },
  quotas: {
    title: 'Quotas and capacity',
    subtitle: 'The tenant’s limits and how much of them it uses.',
    listTitle: 'Quotas',
    enforcement:
      'Every launch is checked: a run that would go over a limit is refused, with the limit and the usage. vCPU and memory in use count the running runs and workspaces. Only the platform administrator raises a limit; “Request more” opens a ticket for them.',
    searchPlaceholder: 'Search quotas…',
    meterLabel: 'Usage of the {{name}} quota',
    columns: { name: 'Quota', description: 'What it limits', scope: 'Scope', usage: 'Used / limit' },
    scopes: {
      default: 'Platform default',
      sku: 'Plan',
      tenant: 'Set by the platform',
    },
    scopeHints: {
      default: 'The default limit of every organisation.',
      sku: 'The limit of the subscribed plan.',
      tenant: 'Set for this organisation by the platform administrator.',
    },
    fields: { level: 'Level', sku: 'Plan' },
    empty: { title: 'No quotas', description: 'This tenant has no declared limit.' },
    errors: { integer: 'A whole number.', aboveLimit: 'Above the current limit ({{limit}}).' },
    detail: {
      tabs: { summary: 'Summary', increase: 'Request more' },
      pending: 'Pending requests',
      noPending: 'No open increase request.',
    },
    increase: {
      hint: 'A request opens a support ticket: the operator raises the limit after checking.',
      newValue: 'New limit',
      newValueHelp: 'Current limit: {{limit}}.',
      reason: 'Why?',
      reasonHelp: 'The need, how long, what happens without it: the operator decides on that basis.',
      submit: 'Send the request',
      sent: 'Request sent: a ticket was opened.',
      ticketTitle: 'Raise the “{{name}}” quota to {{value}}',
    },
  },
  quotaExceeded: {
    full: 'Quota exceeded: {{label}} ({{key}}) — limit {{max}}, already in use {{current}}, requested {{requested}}. Stop a workspace or a run, pick a smaller instance type, or ask for more quota.',
    short:
      'Quota exceeded: {{label}} ({{key}}). Stop a workspace or a run, pick a smaller instance type, or ask for more quota.',
    unknownKey: 'unknown quota',
    keys: {
      'runs.cpu.max': 'vCPU in use across the organisation',
      'runs.memory.max': 'memory in use across the organisation',
      'job.cpu.max': 'vCPU of a single run or workspace',
      'job.memory.max': 'memory of a single run or workspace',
    },
  },
  tickets: {
    title: 'Support',
    subtitle: 'The tenant’s tickets: incidents and quota requests.',
    searchPlaceholder: 'Search tickets…',
    new: 'New ticket',
    newTitle: 'New ticket',
    newSubtitle: 'Describe the incident: what happens, since when, what you tried.',
    submit: 'Open the ticket',
    created: 'Ticket opened.',
    columns: { title: 'Ticket', kind: 'Kind', status: 'Status', created: 'Opened' },
    statuses: { OPEN: 'Open', CLOSED: 'Closed' },
    kinds: {
      issue: 'Incident',
      quota: 'Quota {{key}} → {{value}}',
      subscription: 'Subscription',
    },
    severities: { p1_minimal: 'Minor', p2_moderate: 'Moderate', p3_critical: 'Critical' },
    resources: {
      job: 'Job',
      workflow: 'Workflow',
      workspace: 'Workspace',
      endpoint: 'Serving endpoint',
      datawarehouse: 'SQL warehouse',
      bucket: 'Bucket',
      infrastructure: 'Infrastructure',
    },
    fields: {
      title: 'Title',
      titlePlaceholder: 'The conso-regionale run stays queued',
      severity: 'Severity',
      severityHelp: 'Critical: production is down. Moderate: degraded. Minor: an annoyance.',
      resourceType: 'Affected resource',
      resourceId: 'Resource identifier',
      resourceIdHelp: 'The one in its detail URL, for example job_8f2c.',
      description: 'Description',
    },
    empty: { title: 'No tickets', description: 'An incident or a quota request opens a ticket.' },
    detail: {
      subtitle: 'Support ticket of the tenant.',
      tabs: { summary: 'Summary', definition: 'Definition' },
      quota: 'Requested quota',
      close: 'Close',
      reopen: 'Reopen',
      closed: 'Ticket closed.',
      reopened: 'Ticket reopened.',
      delete: 'Delete',
      confirmDelete: 'Delete the ticket “{{name}}”? Its history is lost.',
      deleted: 'Ticket “{{name}}” deleted.',
    },
  },
  firewall: {
    title: 'Firewall',
    subtitle:
      'The addresses allowed to reach the platform, and the destinations it may reach. Today, no rule is applied to traffic yet.',
    searchPlaceholder: 'Search rules…',
    new: 'New rule',
    newTitle: 'New rule',
    newSubtitle: 'An ingress rule allows an external address; an egress rule, an external destination.',
    created: 'Rule created.',
    columns: {
      type: 'Direction',
      source: 'Source',
      target: 'Target',
      ports: 'Ports',
      description: 'Description',
    },
    types: { ingress: 'Ingress', egress: 'Egress' },
    protocols: { tcp: 'TCP', udp: 'UDP', any: 'Any' },
    targets: { console: 'Console', api: 'API', mcp: 'MCP server', 'graal-run': 'Runs (graal-run)' },
    fields: {
      type: 'Direction',
      protocol: 'Protocol',
      ports: 'Ports',
      portsHelp: '443, 80,443, 8000-8080 or * for all.',
      source: 'External source',
      sourceHelp: 'An IPv4 address or a CIDR block, for example 203.0.113.0/24.',
      target: 'Target on the platform',
      egressSource: 'Source on the platform',
      egressTarget: 'External destination',
      egressTargetHelp: 'An IPv4 address or a CIDR block, for example 198.51.100.0/24.',
      description: 'Description',
    },
    errors: {
      ports: 'A port from 1 to 65535, a comma-separated list, an increasing range, or *.',
      address: 'An IPv4 address or a CIDR block (a.b.c.d/n).',
      ip: 'An IPv4 address (a.b.c.d).',
    },
    empty: {
      title: 'No rules',
      description: 'Without rules, nothing tells an allowed address from another.',
    },
    check: {
      title: 'Check an address',
      hint: 'The same check as the backend: an ingress rule whose source contains the address allows it.',
      ip: 'IPv4 address',
      ipHelp: 'The public address of a workstation or a site.',
      submit: 'Check',
      granted: '{{ip}} is allowed.',
      denied: '{{ip}} is not allowed by any rule.',
    },
    detail: {
      tabs: { summary: 'Summary', definition: 'Definition' },
      delete: 'Delete',
      confirmDelete: 'Delete the rule “{{name}}”?',
      deleted: 'Rule “{{name}}” deleted.',
    },
  },
  infrastructures: {
    title: 'Infrastructures',
    subtitle: 'The targets where jobs, workspaces and endpoints run.',
    searchPlaceholder: 'Search infrastructures…',
    new: 'New infrastructure',
    newTitle: 'New infrastructure',
    newSubtitle: 'Declare an execution target and the instance types it accepts.',
    inCluster: 'in the cluster',
    connectionHint:
      'graal connects to the platform cluster (its in-cluster identity) or to the cluster of a kubeconfig, and acts only with the rights that kubeconfig grants in the chosen namespace. Those rights are checked before saving; the kubeconfig is never shown again.',
    byKubeconfig: 'by kubeconfig',
    modes: {
      inCluster: 'Platform cluster (in-cluster)',
      kubeconfig: 'Kubeconfig',
    },
    namespaceHelp:
      'Empty = the platform namespace. graal acts only with the rights this kubeconfig grants in this namespace; it never creates it.',
    namespacePlaceholder: 'platform namespace',
    kubeconfigHelp:
      'Paste the YAML or import the file (.yaml, .kubeconfig). Read in the browser, sent once, never shown again.',
    kubeconfigKeep: 'Leave empty to keep the saved kubeconfig (it is never shown again).',
    kubeconfigKeepPlaceholder: 'Saved kubeconfig — leave empty to keep it',
    contextHelp: 'Optional: the kubeconfig context to use; empty = its current context.',
    platformNamespace: 'platform namespace',
    currentContext: 'current context of the kubeconfig',
    created: 'Infrastructure “{{name}}” created.',
    publicIps: 'Public addresses',
    noInstanceType: 'No instance type allowed.',
    columns: {
      name: 'Name',
      target: 'Target',
      location: 'Location',
      instanceTypes: 'Instance types',
    },
    fields: {
      name: 'Name',
      location: 'Location',
      country: 'Country',
      description: 'Description',
      mode: 'Connection',
      namespace: 'Namespace',
      kubeconfig: 'Kubeconfig',
      context: 'Context',
    },
    services: {
      scheduler: 'Scheduler',
      logging: 'Logs',
      workflow: 'Workflows',
      nvidia: 'NVIDIA GPU',
      runtimes: 'Distributed runtimes',
    },
    errors: {
      noInstanceType: 'Allow at least one instance type.',
      namespace:
        'Invalid namespace: lowercase letters, digits and “-”, starting and ending with a letter or a digit, 63 characters at most.',
    },
    empty: {
      title: 'No infrastructure',
      description: 'Without an infrastructure, no run can be scheduled.',
    },
    detail: {
      tabs: {
        summary: 'Summary',
        capacity: 'Capacity',
        services: 'Services',
        instanceTypes: 'Instance types',
        check: 'Kubernetes rights',
        definition: 'Definition',
        access: 'Access',
      },
      accessHint: 'Who can read, edit or run on this infrastructure.',
      delete: 'Delete',
      confirmDelete: 'Delete the infrastructure “{{name}}”? Runs targeting it will fail.',
      deleted: 'Infrastructure “{{name}}” deleted.',
    },
    trust: {
      label: 'Trust',
      trusted: 'Trusted',
      untrusted: 'Untrusted cluster',
      help: 'A tenant administrator wrote the Kubernetes target of this infrastructure (kubeconfig, namespace, context or mode): the cluster is controlled by the tenant. graal sends it no secret of the platform or of the tenant (job identity, object storage keys, image registry credentials, passwords) and refuses workspaces, endpoints, streams and data warehouses on it.',
      warning:
        'You are not a platform administrator: setting a kubeconfig, a namespace or a context, or changing the connection mode, will make this infrastructure untrusted. graal will then send it no secret (job identity, object storage keys, image registry credentials, passwords) and refuse workspaces, endpoints, streams and data warehouses on it.',
    },
    unreachableReasons: {
      unreachable: 'API server unreachable',
      tls: 'TLS certificate refused',
      http: 'HTTP response {{code}}',
      serverNotAllowed: 'address not allowed',
      invalidKubeconfig: 'invalid kubeconfig',
    },
    serverErrors: {
      insufficientRights:
        'This kubeconfig lacks required rights in namespace “{{namespace}}”. Missing rights: {{rights}}.',
      insufficientRightsUnknown:
        'This kubeconfig lacks required rights in the namespace. “Check rights” on the infrastructure details lists them.',
      unreachable: 'The API server {{server}} cannot be used with this kubeconfig: {{cause}}.',
      unreachableNoServer: 'This kubeconfig cannot be used: {{cause}}.',
      unreachableUnknown:
        'The API server cannot be reached with this kubeconfig, or it refuses its credentials.',
      platformAdminOnly:
        'Only the platform administrator can set a kubeconfig on a shared infrastructure, or point graal’s own identity at another namespace.',
      tenantAdminOnly: 'Only a tenant (or platform) administrator can set a kubeconfig.',
      serverNotAllowed:
        'This API server is not allowed for a tenant administrator: only a public API server, or one allowed by the platform, is accepted.',
      kubeconfigRequired:
        'The credentials of this infrastructure were set by the platform: to change only the namespace or the context, include your own kubeconfig in the same change.',
      invalidKubeconfig:
        'Kubeconfig refused: unreadable YAML, unknown context, or a forbidden mechanism (exec plugin, local file).',
      notKubernetes: 'This infrastructure has no Kubernetes target: no rights to check.',
    },
    check: {
      hint: 'Asks the API server, with the saved kubeconfig, for every right graal needs in the effective namespace.',
      run: 'Check rights',
      running: 'Checking…',
      failed: 'Check failed',
      idle: 'No check run yet.',
      status: 'Verdict',
      ready: 'Ready',
      insufficient: 'Insufficient rights',
      server: 'API server',
      namespace: 'Effective namespace',
      rightsTitle: 'Checked rights',
      rightsHint:
        'A refused required right prevents runs; an informative right only affects the features that need it.',
      noRights: 'The server returned no rights.',
      required: 'Required',
      optional: 'Informative',
      allowed: 'Granted',
      denied: 'Refused',
      platformOnly:
        'Only the platform administrator checks the rights of a shared infrastructure or of graal’s own identity (modes in-cluster and frompod).',
      columns: {
        resource: 'Resource',
        verb: 'Verb',
        required: 'Required',
        allowed: 'Allowed',
        reason: 'Reason',
      },
    },
  },
  instanceTypes: {
    title: 'Instance types',
    subtitle: 'The resource templates a job, a workspace or an endpoint asks for.',
    searchPlaceholder: 'Search types…',
    columns: {
      id: 'Type',
      family: 'Family',
      cpus: 'vCPU',
      memory: 'Memory',
      storage: 'Storage',
      gpus: 'GPU',
      accepted: 'Infrastructures',
    },
    fields: { generation: 'Generation' },
    detail: {
      tabs: { summary: 'Summary', infrastructures: 'Infrastructures' },
      noInfrastructure: 'No infrastructure of the tenant accepts this type.',
    },
  },
  runtimes: {
    title: 'Runtimes',
    subtitle:
      'The execution environments of jobs: the platform catalog, and your runtimes built on one of its base images.',
    searchPlaceholder: 'Search runtimes…',
    new: 'New runtime',
    newTitle: 'New runtime',
    newSubtitle:
      'A platform base image plus your pip packages: built without root in your infrastructure, pushed to your organization’s registry.',
    created: 'Runtime “{{name}}” created.',
    columns: {
      name: 'Runtime',
      technicalName: 'Technical name',
      status: 'Status',
      enabled: 'Enabled',
      scope: 'Origin',
    },
    scopes: { platform: 'Platform', tenant: 'Organization' },
    fields: {
      name: 'Name',
      technicalName: 'Technical name',
      technicalNameHelp: 'Lowercase letters, digits, . _ -.',
      description: 'Description',
      base: 'Base image',
      baseHelp: 'A platform image: the layer of your packages is added on top of it, no Dockerfile.',
      bases: { python: 'Python (graal-runtime/python)' },
      project: 'Project',
      projectHelp: 'Builds run and are accounted in this project.',
      dockerfile: 'Dockerfile',
    },
    errors: {
      imageName: 'Lowercase letters and digits, separated by . _ or -.',
      dependencies:
        'One line per package, “name==version” with an EXACT version (e.g. pandas==2.2.3); no option, URL or duplicate.',
    },
    empty: { title: 'No runtimes', description: 'The tenant’s runtimes will show up here.' },
    versions: {
      title: 'Versions',
      empty: 'No versions.',
      new: 'New version',
      newTitle: 'New version',
      newSubtitle:
        'The pip packages of this version, pinned: another list is another version. Only wheels are installed.',
      created: 'Version “{{name}}” created.',
      columns: { name: 'Version', image: 'Image', built: 'Built image' },
      fields: {
        name: 'Name',
        technicalName: 'Technical name',
        description: 'Description',
        dependencies: 'Dependencies',
        dependenciesHelp: 'One line per package: name==version.',
      },
      image: 'Built image',
      imageHelp: 'Pinned by digest: put it in the “Docker image” (docker_image) of a job.',
      notBuilt: 'Not built yet.',
      build: 'Build',
    },
    dependencies: {
      title: 'Dependencies',
      reference: 'Package',
      version: 'Version',
      empty: 'No declared dependency.',
    },
    builds: {
      title: 'Builds',
      warning:
        'A build installs the packages of the version into the base image, without root, then pushes the image to your organization’s registry. The platform catalog is built by its CI.',
      start: 'Build',
      started: 'Build {{name}} started.',
      empty: 'No builds.',
      logs: 'Log',
      noLogs: 'No log lines.',
      columns: { name: 'Build', initiator: 'Requested by' },
      newTitle: 'Build a version',
      newSubtitle:
        'The Job runs in the chosen infrastructure (a platform infrastructure: the push credential never leaves it).',
      fields: { version: 'Version', infrastructure: 'Infrastructure' },
      image: 'Produced image',
      noImage: 'Available at the end of the build.',
      catalog: 'The platform catalog is built by its CI, not from the console.',
    },
    detail: {
      tabs: { summary: 'Summary', versions: 'Versions', builds: 'Builds' },
      delete: 'Delete',
      confirmDelete: 'Delete the runtime “{{name}}”? Jobs that use it will no longer start.',
      deleted: 'Runtime “{{name}}” deleted.',
    },
  },
  devices: {
    title: 'Devices',
    subtitle: 'The edge sites attached to the tenant: boxes close to the sensors that run jobs on site.',
    searchPlaceholder: 'Search devices…',
    columns: {
      presence: 'Status',
      name: 'Device',
      location: 'Location',
      target: 'Target',
      mode: 'Mode',
      agent: 'Agent version',
      lastSeen: 'Last contact',
    },
    presence: { online: 'Online', offline: 'Offline', untracked: 'Not tracked' },
    modes: { agent: 'Agent (outbound 443)', kubeconfig: 'Kubeconfig (private network)' },
    filters: { label: 'Presence', all: 'All', online: 'Online', offline: 'Offline' },
    fields: {
      name: 'Name',
      location: 'Location',
      country: 'Country',
      description: 'Description',
      instanceTypes: 'Instance types',
    },
    empty: {
      title: 'No devices',
      description:
        'A site shows up here as soon as the install command has been pasted on its box: “Add an edge site”.',
    },
    edge: {
      action: 'Add an edge site',
      title: 'Add an edge site',
      subtitle:
        'Name the site: the console gives you the command to paste on the Linux box. It registers by itself, with nothing opened inbound.',
      name: 'Site name',
      nameHelp: 'What will identify the site in the console.',
      namePlaceholder: 'Saumur substation',
      location: 'Location',
      locationPlaceholder: 'Saumur, cabinet A',
      submit: 'Get the command',
      created: 'Install command ready.',
      commandTitle: 'Paste this command on the site’s Linux box',
      copy: 'Copy the command',
      copiedLabel: 'Copied',
      copied: 'Command copied.',
      noCommand:
        'The platform has no API address configured: it cannot compose the command. Enrollment code: {{code}} — hand it to the platform administrator.',
      expiry: 'Code {{code}}, single use, valid for 24 h (until {{date}}).',
      once: 'Shown only once: leaving this screen loses it.',
      network: 'Only network requirement: outbound 443 to {{host}}, proxy accepted. Nothing to open inbound.',
      waiting: 'Waiting for “{{name}}”…',
      waitingHint:
        'Paste the command on the box: this page moves on by itself as soon as the site registers.',
      expired: 'The code expired without any site registering: add the site again.',
      arrived: '“{{name}}” is online.',
      arrivedHint: 'The code is consumed: the command is no longer usable.',
      view: 'View the site',
      another: 'Add another site',
      agentOff:
        'Agent mode is not enabled on this platform: no code was issued. The kubeconfig-mode certificate below was issued instead.',
    },
    kubeconfig: {
      title: 'Kubeconfig mode (private network)',
      hint: 'The former path: a PKCS#12 certificate, the platform drives the site’s cluster over a private network.',
    },
    trust: {
      label: 'Trusted device',
      help: 'A trusted device may receive the identity of the jobs it runs (access to the catalog, libraries and project data under that identity). The tenant’s storage keys never go there. Untrusted: a job that needs an identity is refused there (DEVICE_NOT_TRUSTED).',
      agentNote:
        'Agent mode (v1): no identity goes to the site yet; this setting takes effect with the agent’s identity relay.',
      isTrusted: 'This device is trusted.',
      isNotTrusted: 'This device is not trusted.',
      reserved: 'Only the platform administrator can change this setting.',
      granted: 'Device marked as trusted.',
      revoked: 'Device removed from trusted devices.',
      failed: 'The setting was not changed: {{detail}}',
      confirmTitle: 'Mark “{{name}}” as trusted?',
      confirm:
        'Jobs running there will receive their identity: whoever controls this box can act under that identity while the run lasts. Reserve it for a site your organisation administers.',
      confirmLabel: 'Mark as trusted',
    },
    delivery: {
      title: 'Delivery to the edge site',
      at: '{{state}} {{time}}',
      steps: {
        queued: 'Queued',
        published: 'Sent',
        accepted: 'Accepted',
        running: 'Running',
        finished: 'Finished',
      },
      states: {
        queued: 'Queued',
        published: 'Sent',
        accepted: 'Accepted',
        rejected: 'Rejected',
        expired: 'Expired',
        withdrawn: 'Withdrawn',
      },
      stepState: { done: 'done', current: 'in progress', todo: 'to come', failed: 'failed' },
      failure: {
        rejected: 'The site rejected the command: it falls outside its local policy.',
        expired:
          'The command was not delivered before its deadline (24 h by default): the site stayed offline. The run failed and will not start.',
        withdrawn: 'The command was withdrawn from the queue before it was sent (run cancelled).',
      },
      otherReason: 'Reason:',
      reasons: {
        RUN_EXPIRED: 'Deadline passed before delivery.',
        RUN_REJECTED: 'Rejected by the site.',
        RUN_KILLED: 'Run cancelled.',
        POLICY_REGISTRY_DENIED: 'Image registry not allowed by the site.',
        REGISTRY_NOT_ALLOWED: 'Image registry not allowed.',
        POLICY_DIGEST_REQUIRED: 'The site requires an image pinned by digest (@sha256).',
        POLICY_TYPE_DENIED: 'Job type not allowed by the site.',
        POLICY_CPU_EXCEEDED: 'Requested CPU above the site’s ceiling.',
        POLICY_MEMORY_EXCEEDED: 'Requested memory above the site’s ceiling.',
        POLICY_TIMEOUT_EXCEEDED: 'Maximum duration above the site’s ceiling.',
        SPEC_INVALID: 'Run specification unreadable by the agent.',
        UNKNOWN_KID: 'Platform signature unknown to the site (key not approved).',
        UNKNOWN_KIND: 'Command unknown to this agent version.',
      },
    },
    enroll: {
      subtitle: 'The console issues a certificate; the device uses it to register with the platform.',
      description: 'Intended device',
      descriptionHelp: 'What will identify the enrollment: site, serial number…',
      descriptionPlaceholder: 'Cholet substation, cabinet B',
      submit: 'Issue the certificate',
      created: 'Certificate issued.',
      issued: 'Device certificate',
      issuedHint:
        'Base64-encoded PKCS#12 store, single use, valid for 7 days: put it on the device, whose agent presents it to the platform.',
      copy: 'Copy the certificate',
      download: 'Download (.p12)',
      command:
        'On the device, the graal agent (devices/agent) enrolls with this file, then declares its cluster; the enrollment is consumed:',
      pending: 'Pending enrollments',
      pendingHint: 'Issued certificates no device has used yet.',
      none: 'No pending enrollment.',
      revoke: 'Revoke the enrollment',
      confirmRevoke: 'Revoke the enrollment “{{name}}”? Its certificate will no longer allow enrolling.',
      revoked: 'Enrollment revoked.',
      columns: { description: 'Intended device' },
    },
    detail: {
      tabs: { summary: 'Summary', definition: 'Definition', access: 'Access' },
      delete: 'Delete',
      confirmDelete: 'Delete the device “{{name}}”? It will have to enroll again.',
      deleted: 'Device “{{name}}” deleted.',
    },
  },
  bridges: {
    title: 'Bridges',
    subtitle:
      'The graal YARN bridge: an unmodified Hadoop or Spark client submits its applications to it as to a ResourceManager, and each becomes a run of the bridge’s project. Reachable only if the platform has deployed the YARN component.',
    new: 'New bridge',
    newTitle: 'New bridge',
    newSubtitle:
      'The project, identity, infrastructure and bucket under which your Hadoop clients’ YARN applications will run.',
    created: 'Bridge created.',
    columns: { description: 'Bridge', type: 'Type', owner: 'Owner' },
    types: { yarn: 'YARN (Hadoop)' },
    fields: {
      type: 'Type',
      description: 'Description',
      project: 'Project',
      projectHelp:
        'Applications become runs of the “yarn-<bridge>” job of this project. You must be able to edit this project.',
      identity: 'Identity',
      identityHelp:
        'Applications run under this identity, with its own permissions only. You must be able to use it (/identity/use).',
      infrastructure: 'Infrastructure',
      infrastructureHelp: 'The cluster where the applications’ pods run.',
      bucket: 'Bucket',
      bucketHelp:
        'The S3 bucket for staging and data (fs.defaultFS = s3a://<name>). It must carry the project’s graal.systems/project label, and the identity must be able to read and write it.',
      mode: 'Mode',
      modeHelp:
        'Distributed: one pod per YARN container (MapReduce and Spark). Local: a single pod replays the job (MapReduce only).',
      instanceType: 'Instance type',
      instanceTypeHelp: 'The size of each application’s main pod.',
      s3Endpoint: 'S3 endpoint',
      s3EndpointHelp:
        'The S3 endpoint as the client machine reaches it, written to core-site.xml (fs.s3a.endpoint).',
    },
    modes: { default: 'Platform default', distributed: 'Distributed', local: 'Local' },
    instanceTypes: { auto: 'Automatic (smallest without GPU)' },
    errors: {
      endpoint: 'An http:// or https:// URL.',
      identityNotUsable: 'You cannot use this identity: /identity/use is required.',
    },
    empty: {
      title: 'No bridges',
      description: 'A bridge lets an existing Hadoop or Spark client submit its applications to graal.',
    },
    detail: {
      tabs: { summary: 'Summary', definition: 'Definition', configuration: 'Client files', access: 'Access' },
      status:
        'An unmodified Hadoop 3.x or Spark client submits to this bridge with the files of the “Client files” tab. The bridge is reachable only if the platform has deployed the YARN component; otherwise the client fails to connect.',
      delete: 'Delete',
      confirmDelete: 'Delete the bridge “{{name}}”? Its tokens are revoked.',
      deleted: 'Bridge “{{name}}” deleted.',
    },
    files: {
      title: 'Hadoop client files',
      download: 'Download',
      tokenHint:
        'Binary file to keep secret: it acts under the bridge’s identity. Its lifetime is limited by the platform: download it again when it expires.',
      tokenMissing:
        'Token file not rendered: you must be able to edit this bridge and use its identity. Without it, the bridge rejects the client.',
      tokenInvalid: 'The token file received is unreadable (invalid base64).',
      s3Hint:
        'S3 keys of the bridge’s bucket (AWS_* variables, read by S3A from the client environment). They remain valid after the token expires and after your rights are removed: keep them secret, never in an XML file.',
      s3Missing:
        'S3 keys not rendered: the bridge’s identity must be able to read and edit its bucket. Without them, the client sets its own keys.',
      usageTitle: 'Usage',
      usage:
        'Put the three XML files in the folder named by HADOOP_CONF_DIR and the token file in HADOOP_TOKEN_FILE_LOCATION, then submit as to a ResourceManager:',
      usageS3:
        'The client reads and writes the bucket with the keys of graal-s3.env, loaded into its environment; failing that, with its own keys (fs.s3a.access.key and fs.s3a.secret.key).',
      empty: 'No file rendered for this bridge.',
    },
  },
  streams: {
    title: 'Streams',
    subtitle: 'The event streams jobs produce and consume.',
    searchPlaceholder: 'Search streams…',
    new: 'New stream',
    newTitle: 'New stream',
    newSubtitle:
      'A Kafka or Pulsar that graal deploys on the tenant’s infrastructure, or an existing stream.',
    created: 'Stream “{{name}}” created.',
    columns: { name: 'Stream', engine: 'Engine', status: 'Status', description: 'Description' },
    engines: { kafka: 'Managed Kafka', pulsar: 'Managed Pulsar', external: 'External' },
    fields: {
      name: 'Name',
      nameHelp: 'Lowercase letters, digits, . _ -.',
      engine: 'Engine',
      engineHelp:
        'Managed: a Kafka node (KRaft) or a standalone Pulsar deployed by graal, reachable by the tenant’s runs. External: a stream that already exists.',
      infrastructure: 'Infrastructure',
      infrastructureHelp: 'The cluster the engine runs on.',
      identity: 'Identity',
      identityHelp: 'Machine account the stream belongs to (required).',
      instanceType: 'Instance type',
      instanceTypeHelp: 'A single node, capped at 2 vCPU and 4 GiB.',
      conf: 'Kafka configuration',
      confHelp:
        'One key=value line per broker setting. Listeners, quorum, directories and memory are set by the platform.',
      username: 'Username',
      password: 'Password',
      passwordHelp: 'Sealed in the tenant’s vault; it cannot be read back.',
      description: 'Description',
    },
    errors: {
      name: 'Lowercase letters, digits, . _ -, 249 characters at most.',
      conf: 'One key=value line per setting, the key in lowercase (log.retention.hours).',
    },
    refusals: {
      identity: 'Choose the stream’s identity: the server requires it.',
      reservedKey: 'The key “{{key}}” is set by the platform: remove it from the configuration.',
      invalidKey: 'The key “{{key}}” is not a Kafka configuration key.',
      invalidValue: 'The value of “{{key}}” is too long.',
      tooManyKeys: 'Too many settings: 64 at most.',
      pulsarConf: 'A managed Pulsar takes no configuration.',
      instanceType: 'This instance type does not exist on this tenant.',
      invalid: 'The server refused this stream.',
    },
    empty: { title: 'No streams', description: 'The tenant’s event streams will show up here.' },
    detail: {
      tabs: { summary: 'Summary', definition: 'Definition', access: 'Access' },
      version: 'Engine version',
      address: 'Bootstrap address',
      owner: 'Owner',
      managedNote:
        'A single node, reachable by this tenant’s runs at the bootstrap address. Its data does not survive stopping the stream.',
      delete: 'Delete',
      confirmDelete: 'Delete the stream “{{name}}”? Its events are lost.',
      deleted: 'Stream “{{name}}” deleted.',
    },
  },
  workspaces: {
    title: 'Workspaces',
    subtitle:
      'Jupyter, VS Code, RStudio, Grafana, Superset, Metabase and Zeppelin, opened through the application gateway.',
    kindSubtitle: {
      jupyter: 'The tenant’s Jupyter notebooks, opened through the application gateway.',
      vscode: 'The tenant’s VS Code editors, opened through the application gateway.',
      rstudio: 'The tenant’s RStudio sessions, opened through the application gateway.',
    },
    searchPlaceholder: 'Search workspaces…',
    new: 'New workspace',
    newTitle: 'New workspace',
    newSubtitle:
      'An interactive editor on the tenant’s infrastructure, with the bucket and Trino already wired.',
    created: 'Workspace “{{name}}” created: it is starting.',
    open: 'Open',
    openFailed: 'Could not open the workspace',
    power: {
      start: 'Start',
      stop: 'Stop',
      starting: 'Workspace “{{name}}”: start requested.',
      stopping: 'Workspace “{{name}}”: stop requested.',
      startFailed: 'The workspace did not start',
      stopFailed: 'The workspace did not stop',
      forbidden: 'You need the right to edit this workspace, or its project.',
      notFound: 'This workspace no longer exists.',
    },
    starting: 'Starting…',
    notRunning: 'Stopped',
    soon: '{{type}} — coming soon',
    types: {
      jupyter: 'Jupyter',
      vscode: 'VS Code',
      rstudio: 'RStudio',
      grafana: 'Grafana',
      superset: 'Superset',
      metabase: 'Metabase',
      zeppelin: 'Zeppelin',
      'sas-studio': 'SAS Studio',
    },
    columns: {
      name: 'Workspace',
      type: 'Type',
      status: 'Status',
      instanceType: 'Instance type',
      owner: 'Owner',
      project: 'Project',
    },
    fields: {
      type: 'Type',
      name: 'Name',
      instanceType: 'Instance type',
      infrastructure: 'Infrastructure',
      infrastructureHelp: 'The tenant’s only infrastructure: it is selected for you.',
      description: 'Description',
      version: 'Version',
      publicUrl: 'Address',
      project: 'Project',
      projectHelp: 'Whoever can edit the project can open, edit, start and stop the workspace.',
      noProject: 'No project',
    },
    files: {
      hint: 'Upload files into the workspace (notebooks, sample data), under the chosen folder.',
      path: 'Folder',
      input: 'Files to upload',
      upload: 'Upload files',
      invalidPath: 'An absolute path, without “..”.',
      uploaded_one: '{{count}} file uploaded to {{path}}.',
      uploaded_other: '{{count}} files uploaded to {{path}}.',
      failed: 'Upload refused: {{detail}}',
    },
    empty: { title: 'No workspaces', description: 'Create a Jupyter or a VS Code to explore the data.' },
    detail: {
      tabs: {
        summary: 'Summary',
        logs: 'Log',
        files: 'Files',
        definition: 'Definition',
        access: 'Access',
      },
      gateway:
        'The workspace opens in a new tab, behind the application gateway, which authenticates you before showing the editor.',
      noLogs: 'No log lines.',
      delete: 'Delete',
      confirmDelete: 'Delete the workspace “{{name}}”? Files not saved elsewhere are lost.',
      deleted: 'Workspace “{{name}}” deleted.',
    },
  },
  endpoints: {
    title: 'Endpoints',
    subtitle: 'The models served online by project {{project}}.',
    subtitle_unnamed: 'The models served online by this project.',
    searchPlaceholder: 'Search endpoints…',
    new: 'New endpoint',
    newTitle: 'New endpoint',
    newSubtitle:
      'Serve a version from the model registry: the platform calls it for you, the server is never exposed.',
    created: 'Endpoint “{{name}}” created: it is starting.',
    types: {
      mlflow: 'MLflow',
      sklearn: 'scikit-learn',
      xgboost: 'XGBoost',
      lightgbm: 'LightGBM',
      tfserving: 'TensorFlow Serving',
      torchserve: 'TorchServe',
    },
    columns: {
      name: 'Endpoint',
      type: 'Server',
      model: 'Model',
      replicas: 'Replicas',
      status: 'Status',
      instanceType: 'Instance type',
    },
    fields: {
      name: 'Name',
      type: 'Model format',
      typeHelp:
        'The format of the registered model, which picks the server runtime (MLflow for a model logged with mlflow).',
      model: 'Model',
      modelHelp:
        'A version from this project’s model registry. It no longer changes: to serve another one, create another endpoint.',
      replicas: 'Replicas',
      replicasHelp: 'From 0 to {{max}}; 0 stops the endpoint without deleting it.',
      instanceType: 'Instance type',
      infrastructure: 'Infrastructure',
      description: 'Description',
      publicUrl: 'Address',
      owner: 'Owner',
    },
    empty: { title: 'No endpoints', description: 'Serve a registry model to query it online.' },
    test: {
      hint: 'Sends an inference request to the endpoint, through the platform: the model server’s answer is shown as is.',
      payload: 'Request (JSON)',
      payloadHelp:
        'The MLflow shape: “dataframe_split” (columns and rows), “dataframe_records”, “instances” or “inputs”.',
      invalid:
        'A JSON object with rows: a non-empty “dataframe_split.data”, “dataframe_records”, “instances” or “inputs”.',
      submit: 'Send',
      response: 'Response',
    },
    detail: {
      tabs: {
        summary: 'Summary',
        logs: 'Log',
        test: 'Test',
        definition: 'Definition',
        access: 'Access',
      },
      noLogs: 'No log lines.',
      delete: 'Delete',
      confirmDelete: 'Delete the endpoint “{{name}}”? Applications calling it will get an error.',
      deleted: 'Endpoint “{{name}}” deleted.',
    },
  },
};
